// AdditionalDetailsStep.tsx
"use client"
import type React from "react"
import { useState, useEffect, forwardRef, useImperativeHandle } from "react"
import { useNavigate } from "react-router-dom"
import Button from "../../../components/common/Button"
import Modal from "../../../components/common/Modal"
import useAuth from "../../../hooks/useAuth"
import { createCourseAdditional, updateCourseAdditional } from "../../../api/courseService"
import { Edit, Plus, Trash2, HelpCircle, Settings, Shield, MessageSquare, X } from "lucide-react"

interface FAQItem {
  question: string
  answer: string
}

interface AdditionalDetailsStepProps {
  data: any
  onChange: (data: any) => void
  onNavigateAfterSave?: () => void
  onDirtyChange?: (dirty: boolean) => void
  isEditMode?: boolean // Add this prop
}

export interface AdditionalDetailsStepRef {
  saveAdditionalDetails: (status: string) => Promise<void>;
}

const AdditionalDetailsStep = forwardRef<AdditionalDetailsStepRef, AdditionalDetailsStepProps>(({ data, onChange, onNavigateAfterSave, onDirtyChange, isEditMode = false }, ref) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [removeWatermark, setRemoveWatermark] = useState(false)
  const [faqs, setFaqs] = useState<FAQItem[]>([])
  const [currentFaq, setCurrentFaq] = useState<FAQItem | null>(null)
  const [editingFaqIndex, setEditingFaqIndex] = useState<number | null>(null)
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false)
  const [, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [updateSuccess, setUpdateSuccess] = useState(false)

  // Initialize from data prop when it changes
  useEffect(() => {
    if (data.additional) {
      setRemoveWatermark(data.additional.removeWatermark || false)
      setFaqs(data.additional.FAQ || [])
    } else {
      // Reset if no additional data
      setRemoveWatermark(false)
      setFaqs([])
    }
    setHasChanges(false)
    setUpdateSuccess(false)
  }, [data.additional])

  // Track changes
 useEffect(() => {
  let changed = false;

  if (data.additional) {
    const originalRemoveWatermark =
      data.additional.removeWatermark || false;

    const originalFaqs =
      data.additional.FAQ || [];

    const watermarkChanged =
      removeWatermark !== originalRemoveWatermark;

    const faqsChanged =
      JSON.stringify(faqs) !== JSON.stringify(originalFaqs);

    changed = watermarkChanged || faqsChanged;
  } else {
    changed = removeWatermark || faqs.length > 0;
  }

  setHasChanges(changed);
  onDirtyChange?.(changed);
}, [
  removeWatermark,
  faqs,
  data.additional,
  onDirtyChange
]);


  const handleAddFaq = () => {
    setCurrentFaq({ question: '', answer: '' })
    setEditingFaqIndex(null)
    setIsFaqModalOpen(true)
  }

  const handleEditFaq = (index: number) => {
    setCurrentFaq({ ...faqs[index] })
    setEditingFaqIndex(index)
    setIsFaqModalOpen(true)
  }

  const handleDeleteFaq = (index: number) => {
    const newFaqs = [...faqs]
    newFaqs.splice(index, 1)
    setFaqs(newFaqs)
  }

  const handleSaveFaq = () => {
    if (!currentFaq?.question || !currentFaq?.answer) {
      setError("Both question and answer are required")
      return
    }
    if (currentFaq) {
      const newFaqs = [...faqs]
      if (editingFaqIndex !== null) {
        // Update existing FAQ
        newFaqs[editingFaqIndex] = currentFaq
      } else {
        // Add new FAQ
        newFaqs.push(currentFaq)
      }
      setFaqs(newFaqs)
    }
    setIsFaqModalOpen(false)
    setCurrentFaq(null)
    setEditingFaqIndex(null)
    setError(null)
  }

  const saveAdditionalDetails = async (status: string) => {
    try {
      setLoading(true)
      setError(null)
      
      console.log("Saving additional details with data:", {
        courseId: data._id,
        removeWatermark,
        FAQ: faqs,
        status
      });
      
      if (!user?._id) {
        throw new Error("User authentication failed")
      }
      
      if (!data._id) {
        throw new Error("Course ID is missing")
      }
      
      // Only save if there are changes
      if (!hasChanges) {
        console.log("No changes detected, skipping API call");
        
        // Navigate to course list if saving as draft and callback is provided
        if (status === "Draft" && onNavigateAfterSave) {
          setTimeout(() => {
            onNavigateAfterSave()
          }, 1000)
        } else if (status === "Draft") {
          // Fallback navigation if no callback provided
          setTimeout(() => {
            navigate("/courses")
          }, 1000)
        }
        return;
      }
      
      const payload = {
        courseId: data._id,
        removeWatermark,
        FAQ: faqs,
        status,
        updatedBy: user._id
      }
      
      console.log("API payload:", payload);
      
      let response;
      
      // Check if we have an existing additional details ID
      const additionalId = data.additional?._id;
      
      if (additionalId && isEditMode) {
        // Update existing additional details
        console.log("Updating existing additional details with ID:", additionalId);
        response = await updateCourseAdditional({
          id: additionalId,
          removeWatermark,
          FAQ: faqs,
          user_id: user._id,
        })
      } else {
        // Create new additional details
        console.log("Creating new additional details");
        response = await createCourseAdditional(payload)
      }
      
      console.log("API response:", response);
      
      if (response.status >= 400) {
        throw new Error(response.message || "Failed to save additional details")
      }
      
      // Update the course data with the response
      const updatedData = {
        ...data,
        additional: {
          ...response.data,
          removeWatermark,
          FAQ: faqs,
          status
        }
      }
      
      console.log("Updated course data:", updatedData);
      
      onChange(updatedData)
      setHasChanges(false)
      onDirtyChange?.(false)
      setUpdateSuccess(true)
      
      // Navigate to course list after successful update
      setTimeout(() => {
        if (onNavigateAfterSave) {
          onNavigateAfterSave()
        } else {
          navigate("/courses")
        }
      }, 1000) // Show success message briefly before navigating
      
    } catch (error: any) {
      console.error("Error saving additional details:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Expose the saveAdditionalDetails function to the parent component
  useImperativeHandle(ref, () => ({
    saveAdditionalDetails
  }))

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
        <div className="flex items-center mb-3">
          <div className="bg-indigo-100 rounded-full p-2 mr-3">
            <Settings className="text-indigo-600 w-6 h-6" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{isEditMode ? "Update Your Course" : "Complete Your Course Setup"}</h2>
        </div>
        <p className="text-gray-600">{isEditMode ? "Update the finishing touches to enhance student experience and course security." : "Add the finishing touches to enhance student experience and course security."}</p>
      </div>
      
      {updateSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3 mb-6">
          <div className="bg-green-100 rounded-full p-1 flex-shrink-0">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-green-800">Update Successful!</h4>
            <p className="text-sm text-green-700 mt-1">Your changes have been saved. Redirecting to courses page...</p>
          </div>
        </div>
      )}
      
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-start">
            <div className="bg-blue-100 rounded-full p-2 mr-3 mt-1">
              <Shield className="text-blue-600 w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Watermark Protection</h3>
              <p className="text-sm text-gray-600 max-w-2xl">
                Watermarks display user details on videos to prevent unauthorized use and content theft. 
                {isEditMode ? (
                  <span className="font-medium"> You can update this setting below.</span>
                ) : (
                  <span className="font-medium"> You can enable this feature below.</span>
                )}
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={removeWatermark}
              onChange={(e) => setRemoveWatermark(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
<div
  className={`mt-4 p-4 rounded-lg border ${
    removeWatermark
      ? 'bg-green-50 border-green-200'
      : 'bg-red-50 border-red-200'
  } transition-colors duration-300`}
>
  <div className="flex items-start gap-3">
    <div
      className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full ${
        removeWatermark ? 'bg-green-100' : 'bg-red-100'
      }`}
    >
      {removeWatermark ? (
        <Shield className="text-green-600 w-4 h-4" />
      ) : (
        <X className="text-red-600 w-4 h-4" />
      )}
    </div>

    <p
      className={`flex-1 text-sm leading-5 pt-1 ${
        removeWatermark ? 'text-green-700' : 'text-red-700'
      }`}
    >
      {removeWatermark
        ? "Watermark protection is enabled. User details will be displayed on videos."
        : "Watermark protection is disabled. Videos will not display user details."}
    </p>
  </div>
</div>        
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="bg-purple-100 rounded-full p-2 mr-3">
              <HelpCircle className="text-purple-600 w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h3>
              <p className="text-sm text-gray-600">Add common questions and answers to help students</p>
            </div>
          </div>
          <Button
            onClick={handleAddFaq}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add FAQ
          </Button>
        </div>

        {faqs.length > 0 ? (
          <div className="space-y-4 mt-6">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow duration-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600 text-sm">{faq.answer}</p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEditFaq(index)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      aria-label="Edit FAQ"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteFaq(index)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      aria-label="Delete FAQ"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg mt-6">
            <div className="bg-gray-100 rounded-full p-3 w-12 h-12 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="text-gray-400 w-6 h-6" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No FAQs added yet</h4>
            <p className="text-gray-500 mb-4">Get started by adding your first frequently asked question</p>
            <Button
              onClick={handleAddFaq}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md mx-auto"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Your First FAQ
            </Button>
          </div>
        )}
      </div>

      <Modal
        isOpen={isFaqModalOpen}
        onClose={() => {
          setIsFaqModalOpen(false)
          setCurrentFaq(null)
          setEditingFaqIndex(null)
          setError(null)
        }}
        title={currentFaq?.question ? "Edit FAQ" : "Add FAQ"}
        className="w-full max-w-2xl"
      >
        <div className="space-y-6 p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <div className="bg-red-100 rounded-full p-1 mr-2">
                  <X className="text-red-600 w-3 h-3" />
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Question</label>
            <input
              type="text"
              value={currentFaq?.question || ''}
              onChange={(e) => setCurrentFaq(prev => prev ? ({ ...prev, question: e.target.value }) : null)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
              placeholder="Enter your question here..."
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Answer</label>
            <textarea
              value={currentFaq?.answer || ''}
              onChange={(e) => setCurrentFaq(prev => prev ? ({ ...prev, answer: e.target.value }) : null)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[120px] transition-colors resize-none"
              placeholder="Provide a detailed answer..."
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <Button
              variant="secondary"
              onClick={() => {
                setIsFaqModalOpen(false)
                setCurrentFaq(null)
                setEditingFaqIndex(null)
                setError(null)
              }}
              className="px-6 py-2 border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFaq}
              className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white transition-all duration-200"
            >
              Save FAQ
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
})

export default AdditionalDetailsStep